import { getLotvMath } from "@faro-lotv/lotv";
import { Plane, Vector3, Vector4 } from "three";
import {
  depthPosition,
  DepthSnapshot,
  selectDepthSurface,
  surfaceGeometry,
} from "./depth-surface";
import { SurfaceRequest } from "./surface-wand-context";
import { SurfaceSelection } from "./surface-selection-pass";
import { WorldSurface } from "./world-surface";
import { fitDepthCylinder } from "./fit-depth-cylinder";
import type { CylinderFitter } from "./cylinder-fitting";

const MAX_SEED_DISTANCE = 0.15;
const MIN_NORMAL_LENGTH_SQUARED = 0.9;
const RESIDUAL_PERCENTILE = 0.8;
const MAX_SEED_RESIDUAL = 0.025;
const MIN_PLANE_DISTANCE = 0.03;
const MAX_PLANE_DISTANCE = 0.05;
const MAX_REFINEMENT_SAMPLES = 1024;
const MIN_REFINEMENT_ALIGNMENT = Math.cos(Math.PI / 12);

/**
 * @param snapshot Captured depth grid.
 * @param request Clicked point and camera identity.
 * @param cancelled Whether this request is obsolete.
 * @param fitCylinder Optional worker-backed cylinder fitter; unused for planes.
 * @returns A bounded visible surface patch, or undefined for unstable seeds.
 */
export async function fitDepthSurface(
  snapshot: DepthSnapshot,
  request: SurfaceRequest,
  cancelled: () => boolean,
  fitCylinder?: CylinderFitter,
): Promise<SurfaceSelection | undefined> {
  if (
    !(
      snapshot.cameraInverseViewProjection ?? snapshot.inverseViewProjection
    ).equals(request.inverseViewProjection)
  ) {
    return;
  }
  const viewProjection = snapshot.inverseViewProjection.clone().invert();
  const projected = request.point.clone().applyMatrix4(viewProjection);
  const column = Math.floor((projected.x + 1) * 0.5 * snapshot.width);
  const row = Math.floor((projected.y + 1) * 0.5 * snapshot.height);
  if (
    column < 0 ||
    column >= snapshot.width ||
    row < 0 ||
    row >= snapshot.height
  ) {
    return;
  }
  const seed = row * snapshot.width + column;
  if (
    request.shape === "cylinder" ||
    request.shape === "pipe-run" ||
    request.addTo?.cylinder
  ) {
    const candidates: Array<{
      seed: number;
      origin: Vector3;
      distance: number;
    }> = [];
    for (let vertical = -2; vertical <= 2; vertical++) {
      for (let horizontal = -2; horizontal <= 2; horizontal++) {
        const nextColumn = column + horizontal;
        const nextRow = row + vertical;
        if (
          nextColumn < 0 ||
          nextColumn >= snapshot.width ||
          nextRow < 0 ||
          nextRow >= snapshot.height
        ) {
          continue;
        }
        const nextSeed = nextRow * snapshot.width + nextColumn;
        const depth = snapshot.depth[nextSeed];
        if (!Number.isFinite(depth) || depth <= 0 || depth >= 1) continue;
        const origin = depthPosition(snapshot, nextColumn, nextRow);
        const distance = origin.distanceTo(request.point);
        if (distance <= MAX_SEED_DISTANCE) {
          candidates.push({ seed: nextSeed, origin, distance });
        }
      }
    }
    candidates.sort((left, right) => left.distance - right.distance);
    for (const candidate of candidates.slice(0, 8)) {
      if (cancelled()) return;
      const result = await fitDepthCylinder(
        snapshot,
        request,
        candidate.seed,
        candidate.origin,
        cancelled,
        fitCylinder,
      );
      if (result) return result;
    }
    return;
  }
  if (snapshot.depth[seed] >= 1) return;
  const origin = depthPosition(snapshot, column, row);
  if (origin.distanceTo(request.point) > MAX_SEED_DISTANCE) return;
  const samples: Vector3[] = [];
  for (let vertical = -4; vertical <= 4; vertical++) {
    for (let horizontal = -4; horizontal <= 4; horizontal++) {
      const sampleColumn = column + horizontal;
      const sampleRow = row + vertical;
      if (
        sampleColumn < 0 ||
        sampleColumn >= snapshot.width ||
        sampleRow < 0 ||
        sampleRow >= snapshot.height
      ) {
        continue;
      }
      const depth = snapshot.depth[sampleRow * snapshot.width + sampleColumn];
      if (!Number.isFinite(depth) || depth <= 0 || depth >= 1) continue;
      samples.push(
        depthPosition(snapshot, sampleColumn, sampleRow).sub(origin),
      );
    }
  }
  if (samples.length < 12 || cancelled()) return;
  const math = await getLotvMath();
  const fit = math.fitPlane(
    new Float32Array(samples.flatMap((point) => point.toArray())),
  );
  if (!fit || cancelled()) return;
  const normal = new Vector3(
    fit.normal.x,
    fit.normal.y,
    fit.normal.z,
  ).normalize();
  if (
    !Number.isFinite(normal.x) ||
    normal.lengthSq() < MIN_NORMAL_LENGTH_SQUARED
  ) {
    return;
  }
  const previous = request.addTo;
  if (previous) {
    const fixedPlane = new Plane(
      new Vector3(previous.plane.x, previous.plane.y, previous.plane.z),
      previous.plane.w,
    );
    if (
      previous.cloudId !== request.cloudId ||
      previous.cameraId !== request.cameraId ||
      Math.abs(normal.dot(fixedPlane.normal)) < MIN_REFINEMENT_ALIGNMENT ||
      Math.abs(fixedPlane.distanceToPoint(request.point)) >
        previous.tolerance ||
      Math.abs(fixedPlane.distanceToPoint(origin)) > previous.tolerance
    ) {
      return;
    }
    const region = await selectDepthSurface(
      snapshot,
      seed,
      fixedPlane,
      previous.tolerance,
      cancelled,
    );
    if (!region || cancelled()) return;
    const grid = new WorldSurface(previous);
    await grid.initialize(cancelled);
    await grid.addSelection(
      {
        ...previous,
        ...snapshot,
        viewProjection,
        mask: region.mask,
        worldSpace: false,
      },
      cancelled,
    );
    if (cancelled() || grid.limited || !grid.seedTiles.size) return;
    return grid.selection("growing");
  }
  const plane = new Plane().setFromNormalAndCoplanarPoint(
    normal,
    origin.clone().add(new Vector3(fit.point.x, fit.point.y, fit.point.z)),
  );
  const residuals = samples
    .map((point) => Math.abs(plane.distanceToPoint(point.clone().add(origin))))
    .sort((left, right) => left - right);
  const residual =
    residuals[Math.floor(residuals.length * RESIDUAL_PERCENTILE)];
  if (residual > MAX_SEED_RESIDUAL) return;
  const tolerance = Math.min(
    MAX_PLANE_DISTANCE,
    Math.max(MIN_PLANE_DISTANCE, residual * 3),
  );
  let region = await selectDepthSurface(
    snapshot,
    seed,
    plane,
    tolerance,
    cancelled,
  );
  if (!region || cancelled()) return;
  const refinementSamples: Vector3[] = [];
  const stride = Math.max(1, Math.ceil(region.count / MAX_REFINEMENT_SAMPLES));
  let selectedIndex = 0;
  for (let index = 0; index < region.mask.length; index++) {
    if (!region.mask[index] || selectedIndex++ % stride !== 0) continue;
    refinementSamples.push(
      depthPosition(
        snapshot,
        index % snapshot.width,
        Math.floor(index / snapshot.width),
      ).sub(origin),
    );
  }
  const refined = math.fitPlane(
    new Float32Array(refinementSamples.flatMap((point) => point.toArray())),
  );
  if (refined && !cancelled()) {
    const refinedNormal = new Vector3(
      refined.normal.x,
      refined.normal.y,
      refined.normal.z,
    ).normalize();
    const refinedPlane = new Plane().setFromNormalAndCoplanarPoint(
      refinedNormal,
      origin
        .clone()
        .add(new Vector3(refined.point.x, refined.point.y, refined.point.z)),
    );
    if (
      Math.abs(refinedNormal.dot(normal)) >= MIN_REFINEMENT_ALIGNMENT &&
      Math.abs(refinedPlane.distanceToPoint(origin)) <= tolerance
    ) {
      const expanded = await selectDepthSurface(
        snapshot,
        seed,
        refinedPlane,
        tolerance,
        cancelled,
      );
      if (expanded && expanded.count >= region.count) {
        normal.copy(refinedNormal);
        plane.copy(refinedPlane);
        region = expanded;
      }
    }
  }
  if (cancelled()) return;
  const geometry = surfaceGeometry(snapshot, region.mask, plane, origin);
  if (!geometry.positions.length || cancelled()) return;
  return {
    ...snapshot,
    ...geometry,
    mask: region.mask,
    viewProjection,
    origin,
    plane: new Vector4(normal.x, normal.y, normal.z, plane.constant),
    tolerance,
    cloudId: request.cloudId,
    cameraId: request.cameraId,
  };
}
